import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { select } from 'd3-selection';
import { arc as d3Arc } from 'd3-shape';
import { rgbaFromHex } from './discGeometry';

const POPOVER_HEIGHT = 100;
const MORPH_MS = 900;
// Angular gap between neighbouring skill cells in the zoomed single ring, as a
// fraction of the circle (the at-rest grid uses DrawDisc's CELL_GAP).
const ZOOMED_CELL_GAP = 0.002;
const RING_INSET = 1.1;

// Shared d3 renderer for the disc. The caller supplies a fully laid-out
// `root` tree (from drawMosaicArcs — each node carries normalized x/dx angles
// + y/dy radii) and the visual policy:
//   fill(d, reveal)   → base arc fill (reveal = 0..1 bloom progress, see Disc)
//   hoverFill(d)      → fill while hovered
//   selectedFill(d)   → fill while selected
//   isClickable(d)    → node responds to clicks (and shows a pointer cursor)
//
//   isZoomable(d)     → (optional) clicking the node zooms the disc into it
//
// The SVG is rebuilt only when the tree's STRUCTURE changes (the set of drawn
// node ids). When the same tree arrives with new scores — a student's grades
// revealed — the existing arcs are re-bound to the new data, their geometry
// MORPHS to the new wedge sizes and their fills repaint, instead of the whole
// disc being torn down and replaying its entrance. That is what lets the blank
// white disc animate into colour in place.
//
// ZOOM (Mosaico parity): clicking a zoomable node (the skills disc passes
// areas and competencies) focuses it — the node and its ancestors stretch to
// the full circle, its descendants are rescaled into that span, every other
// branch collapses to nothing — through the same morph. Clicking the focused
// node again, or the disc background, zooms back out. Mosaico's own disc does
// this with a 500 ms tween (DrawMosaic.transitionToNextLevel); here the zoom
// also keeps hover and the skill-click sidebar working inside the zoomed view.
export default function RadialDisc({
  root,
  width,
  height,
  onHover,
  onClick,
  popoverComponent: PopoverComponent,
  fill,
  hoverFill,
  selectedFill,
  isClickable,
  isZoomable,
  reveal = 1,
  popoverWidth = 280,
  errorLabel = 'Disc not available',
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [popoverItem, setPopoverItem] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  // Zoom focus: id of the area/competency filling the disc, or null.
  const [focusId, setFocusId] = useState(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const radius = Math.min(width, height) / 2;
  const nodeId = (d) => d.id || `${d.depth}:${d.name}`;

  // Visual-policy callbacks in a ref so the build effect never depends on
  // their identity (a parent re-render must not rebuild the SVG).
  const cb = useRef({});
  cb.current = { onHover, onClick, fill, hoverFill, selectedFill, isClickable, isZoomable, reveal };

  // The three visible rings, flattened. Memoised on root so the structure key
  // and the data effect see the same array.
  const drawable = useMemo(() => {
    if (!root?.children?.length) return [];
    const nodes = [];
    (function flatten(n) {
      nodes.push(n);
      if (n.children) n.children.forEach(flatten);
    })(root);
    return nodes.filter((d) => d.depth >= 1 && d.depth <= 3);
  }, [root]);
  const structureKey = useMemo(() => drawable.map(nodeId).join('|'), [drawable]);
  // A new tree (different ids) starts zoomed out.
  useEffect(() => setFocusId(null), [structureKey]);

  // Geometry per node id for the current zoom. With no focus this is the laid-
  // out tree as is. With a focus node F: F and its ancestors span the whole
  // circle, F's descendants are rescaled from F's span to the full circle, and
  // every other branch collapses to a zero-width wedge at the side of F it was
  // on (so it slides away rather than popping) and is hidden.
  const viewGeometry = useMemo(() => {
    const byId = new Map(drawable.map((d) => [nodeId(d), d]));
    const f = focusId ? byId.get(focusId) : null;
    const chain = (d) => {
      const ids = new Set();
      for (let n = d; n; n = n.parent) ids.add(nodeId(n));
      return ids;
    };
    const fChain = f ? chain(f) : null;
    const geom = new Map(
      drawable.map((d) => {
        const id = nodeId(d);
        if (!f) return [id, { x: d.x, dx: d.dx, y: d.y, dy: d.dy, hidden: false }];
        if (fChain.has(id)) return [id, { x: 0, dx: 1, y: d.y, dy: d.dy, hidden: false }];
        if (chain(d).has(focusId)) {
          return [id, { x: (d.x - f.x) / (f.dx || 1), dx: d.dx / (f.dx || 1), y: d.y, dy: d.dy, hidden: false }];
        }
        const before = d.x + d.dx / 2 < f.x + f.dx / 2;
        return [id, { x: before ? 0 : 1, dx: 0, y: d.y, dy: d.dy, hidden: true }];
      })
    );
    // Zoomed in, the skill grid is no longer needed: a competency's skills now
    // have a whole circle (or a large share of one) to themselves, so each
    // competency in view lays its skills out as ONE ring across its span,
    // filling the full skill band. (Mosaico keeps its grid when zoomed; this
    // is a deliberate readability improvement for the zoomed view only.)
    if (f) {
      const comps = f.depth === 2 ? [f] : f.depth === 1 ? f.children || [] : [];
      comps.forEach((comp) => {
        const kids = (comp.children || []).filter((k) => geom.has(nodeId(k)));
        if (!kids.length) return;
        const span = geom.get(nodeId(comp));
        const y0 = Math.min(...kids.map((k) => k.y));
        const y1 = Math.max(...kids.map((k) => k.y + k.dy));
        const n = kids.length;
        const gap = Math.min(ZOOMED_CELL_GAP, span.dx / (n * 4));
        const dx = (span.dx - gap * (n - 1)) / n;
        kids.forEach((k, i) => {
          geom.set(nodeId(k), { x: span.x + i * (dx + gap), dx, y: y0, dy: y1 - y0, hidden: false });
        });
      });
    }
    return geom;
  }, [drawable, focusId]);
  const focusNode = useMemo(
    () => (focusId ? drawable.find((d) => nodeId(d) === focusId) ?? null : null),
    [drawable, focusId]
  );

  const arc = useMemo(
    () =>
      d3Arc()
        .startAngle((d) => 2 * Math.PI * d.x)
        .endAngle((d) => 2 * Math.PI * (d.x + d.dx))
        .innerRadius((d) => radius * d.y + RING_INSET)
        .outerRadius((d) => Math.max(radius * d.y + RING_INSET + 1, radius * (d.y + d.dy) - RING_INSET))
        .cornerRadius(2.5),
    [radius]
  );

  function paintNode(el, d, state) {
    const sel = select(el);
    const fillValue =
      state === 'selected'
        ? cb.current.selectedFill(d)
        : state === 'hover'
          ? cb.current.hoverFill(d)
          : cb.current.fill(d, cb.current.reveal);
    const isWhiteTile = fillValue === '#ffffff';
    sel.style('fill', fillValue);
    if (state === 'selected')
      sel
        .style('stroke', isWhiteTile ? '#cbd5e1' : '#ffffff')
        .style('stroke-width', 2)
        .style('filter', `saturate(1.2) drop-shadow(0 2px 8px ${rgbaFromHex(d.color || '#17173a', 0.35)})`);
    else if (state === 'hover')
      sel
        .style('stroke', isWhiteTile ? '#cbd5e1' : '#ffffff')
        .style('stroke-width', 1.5)
        .style('filter', 'saturate(1.18) drop-shadow(0 2px 6px rgba(23, 23, 58, 0.22))');
    else
      sel
        .style('stroke', isWhiteTile ? '#e2e8f0' : 'none')
        .style('stroke-width', isWhiteTile ? 1 : 0)
        .style('filter', 'none');
  }

  function stateFor(d) {
    const id = nodeId(d);
    if (id === selectedId) return 'selected';
    if (id === hoveredId) return 'hover';
    return 'normal';
  }

  // Geometry currently drawn per node id — the morph's starting point.
  const geomRef = useRef(new Map());
  const morphRef = useRef(null);
  const builtKeyRef = useRef(null);

  // ── Build: once per STRUCTURE/size change ──────────────────────────────
  useEffect(() => {
    setLoading(true);
    setNotAvailable(false);
    try {
      if (!drawable.length) {
        setLoading(false);
        return;
      }
      const svg = select(svgRef.current).attr('width', width).attr('height', height);
      svg.selectAll('*').remove();
      const g = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`);

      const paths = g
        .selectAll('path.disc-arc')
        .data(drawable, nodeId)
        .enter()
        .append('path')
        .attr('class', (d) => `disc-arc level-${d.depth}`)
        .attr('d', (d) => arc(d))
        .style('cursor', (d) => (cb.current.isClickable(d) || cb.current.isZoomable?.(d) ? 'pointer' : 'default'))
        // Entrance: each arc fades/scales in, staggered by angular position.
        .style('opacity', 0)
        .style('transform-origin', 'center')
        .style('transform-box', 'fill-box')
        .style('animation-name', 'disc-arc-in')
        .style('animation-duration', '560ms')
        .style('animation-timing-function', 'cubic-bezier(0.22, 1, 0.36, 1)')
        .style('animation-fill-mode', 'forwards')
        .style('animation-delay', (d) => `${Math.round((d.x ?? 0) * 360 + (d.depth ?? 1) * 30)}ms`);

      geomRef.current = new Map(drawable.map((d) => [nodeId(d), { x: d.x, dx: d.dx, y: d.y, dy: d.dy }]));

      const emitHover = (event, d) => {
        if (!svgRef.current) return;
        const offset = 16;
        let left = event.clientX + offset;
        let top = event.clientY - 12;
        if (typeof window !== 'undefined') {
          if (left + popoverWidth > window.innerWidth) left = event.clientX - popoverWidth - offset;
          if (left < 8) left = 8;
          if (top + POPOVER_HEIGHT > window.innerHeight) top = window.innerHeight - POPOVER_HEIGHT - 8;
          if (top < 8) top = 8;
        }
        const payload = {
          ...structuredClone(d),
          posTop: top,
          posLeft: left,
          isClickable: cb.current.isClickable(d),
          assessedSpecificCount: d._assessedCount,
          totalSpecificCount: d._totalCount,
        };
        setPopoverItem(payload);
        cb.current.onHover?.(payload);
      };

      paths
        .on('mouseenter', function (event, d) {
          setHoveredId(nodeId(d));
          emitHover(event, d);
        })
        .on('mousemove', function (event, d) {
          emitHover(event, d);
        })
        .on('mouseleave', function () {
          setHoveredId(null);
          setPopoverItem(null);
        })
        .on('click', function (event, d) {
          event.stopPropagation();
          if (cb.current.isZoomable?.(d)) {
            // Zoom in, or back out when the focused node is clicked again.
            setFocusId((prev) => (prev === nodeId(d) ? null : nodeId(d)));
            setHoveredId(null);
            setPopoverItem(null);
            return;
          }
          if (!cb.current.isClickable(d)) return;
          setSelectedId(nodeId(d));
          cb.current.onClick?.(structuredClone(d));
        });

      // Background click: clear the selection and zoom back out.
      svg.on('click', () => {
        setSelectedId(null);
        setFocusId(null);
        cb.current.onClick?.(undefined);
      });

      g.selectAll('path.disc-arc').each(function (d) {
        paintNode(this, d, stateFor(d));
      });
      builtKeyRef.current = structureKey;
      setLoading(false);
    } catch (e) {
      console.error('Disc render error:', e);
      setLoading(false);
      setNotAvailable(true);
    }
  }, [structureKey, width, height, radius]);

  // ── Data / zoom: same structure, new tree or focus → rebind + morph ────
  useEffect(() => {
    const g = select(svgRef.current).select('g');
    if (g.empty() || !drawable.length || builtKeyRef.current !== structureKey) return undefined;
    const paths = g.selectAll('path.disc-arc').data(drawable, nodeId);

    const from = geomRef.current;
    const to = viewGeometry;
    const changed = drawable.some((d) => {
      const a = from.get(nodeId(d));
      const b = to.get(nodeId(d));
      return (
        !a ||
        Math.abs(a.x - b.x) > 1e-6 ||
        Math.abs(a.dx - b.dx) > 1e-6 ||
        Math.abs(a.y - b.y) > 1e-6 ||
        Math.abs(a.dy - b.dy) > 1e-6
      );
    });
    paths.style('cursor', (d) => (cb.current.isClickable(d) || cb.current.isZoomable?.(d) ? 'pointer' : 'default'));
    // Collapsed branches stop catching the pointer at once; they become
    // invisible only once the morph has shrunk them away (see showHidden).
    paths.style('pointer-events', (d) => (to.get(nodeId(d)).hidden ? 'none' : null));
    const showHidden = (visible) =>
      paths.style('visibility', (d) => (!visible && to.get(nodeId(d)).hidden ? 'hidden' : null));
    paths.each(function (d) {
      paintNode(this, d, stateFor(d));
    });
    if (!changed) return undefined;
    showHidden(true);

    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (morphRef.current) cancelAnimationFrame(morphRef.current);
    if (reduce) {
      paths.attr('d', (d) => arc(to.get(nodeId(d))));
      geomRef.current = to;
      showHidden(false);
      return undefined;
    }
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - start) / MORPH_MS);
      const k = ease(t);
      paths.attr('d', (d) => {
        const a = from.get(nodeId(d)) ?? to.get(nodeId(d));
        const b = to.get(nodeId(d));
        return arc({
          x: a.x + (b.x - a.x) * k,
          dx: a.dx + (b.dx - a.dx) * k,
          y: a.y + (b.y - a.y) * k,
          dy: a.dy + (b.dy - a.dy) * k,
        });
      });
      if (t < 1) morphRef.current = requestAnimationFrame(step);
      else {
        morphRef.current = null;
        geomRef.current = to;
        showHidden(false);
      }
    };
    morphRef.current = requestAnimationFrame(step);
    return () => {
      if (morphRef.current) cancelAnimationFrame(morphRef.current);
      morphRef.current = null;
      geomRef.current = to;
      paths.attr('d', (d) => arc(to.get(nodeId(d))));
      showHidden(false);
    };
  }, [drawable, structureKey, arc, viewGeometry]);

  // ── Restyle in place when hover/selection/reveal changes ───────────────
  useEffect(() => {
    const g = select(svgRef.current).select('g');
    if (g.empty()) return;
    g.selectAll('path.disc-arc').each(function (d) {
      paintNode(this, d, stateFor(d));
    });
  }, [hoveredId, selectedId, reveal]);

  if (notAvailable) {
    return <div className="alert alert-warning">{errorLabel}</div>;
  }

  return (
    <Fragment>
      {loading && <div className="loading-spinner" aria-label="Loading" />}
      <div
        ref={containerRef}
        className={`mosaic-chart ${loading ? 'hide' : ''}`}
        style={{ position: 'relative', width, height, maxWidth: '100%' }}
      >
        <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: '100%', height: 'auto' }} />
        {focusNode && (
          <div className="absolute left-2 top-2 z-10 flex max-w-[85%] items-center gap-2 rounded-full border border-border bg-card/95 py-1 pl-3 pr-1 text-[12px] text-foreground shadow-sm">
            <span className="truncate">
              {focusNode.parent && focusNode.parent.depth >= 1 && (
                <span className="text-muted-foreground">{focusNode.parent.name} › </span>
              )}
              <strong className="font-semibold">{focusNode.name}</strong>
            </span>
            <button
              type="button"
              onClick={() => setFocusId(null)}
              className="shrink-0 rounded-full bg-secondary px-2.5 py-0.5 font-medium text-foreground transition-colors hover:bg-secondary/70"
            >
              Whole disc
            </button>
          </div>
        )}
      </div>
      {mounted &&
        popoverItem &&
        PopoverComponent &&
        createPortal(
          <div
            className="disc-popover"
            style={{
              position: 'fixed',
              top: popoverItem.posTop,
              left: popoverItem.posLeft,
              pointerEvents: 'none',
              zIndex: 10000,
            }}
          >
            <PopoverComponent {...popoverItem} />
          </div>,
          document.body
        )}
    </Fragment>
  );
}
